import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, Phone, Check, Trash2, LogOut, Inbox, ArrowLeft, RefreshCw } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const LoginScreen = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5">
    <div className="absolute inset-0 grid-lines opacity-30" />
    <div className="relative w-full max-w-md p-10 rounded-3xl bg-[#121212] border border-white/10 text-center">
      <img src="/logo-dark.png" alt="Renewolt" className="h-12 mx-auto mb-8 object-contain" />
      <h1 className="font-display font-light text-3xl tracking-tight text-white mb-2">Yönetim Paneli</h1>
      <p className="text-neutral-400 text-sm mb-8">Devam etmek için Google hesabınızla giriş yapın.</p>
      <button
        onClick={login}
        data-testid="google-login-btn"
        className="w-full inline-flex items-center justify-center gap-3 bg-white text-black font-semibold px-6 py-3.5 rounded-full hover:bg-neutral-200 transition-colors duration-200"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        Google ile Giriş
      </button>
      <Link to="/" className="inline-flex items-center gap-1.5 mt-8 text-sm text-neutral-500 hover:text-[#5CC528] transition-colors">
        <ArrowLeft size={14} /> Siteye dön
      </Link>
    </div>
  </div>
);

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState(location.state?.user ? "in" : "checking");
  const [user, setUser] = useState(location.state?.user || null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoadingMsgs(true);
    try {
      const res = await api.get("/contact");
      setMessages(res.data);
    } catch (e) {
      if (e.response?.status === 401) setAuthState("out");
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    const check = async () => {
      if (authState === "in") { fetchMessages(); return; }
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setAuthState("in");
        fetchMessages();
      } catch (e) {
        setAuthState("out");
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/contact/${id}/read`);
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, read: true } : x)));
      toast.success("Okundu olarak işaretlendi");
    } catch { toast.error("İşlem başarısız"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Mesaj silindi");
    } catch { toast.error("Silinemedi"); }
  };

  const doLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/", { replace: true });
  };

  if (authState === "checking") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-4">
        <Loader2 size={40} className="text-[#5CC528] animate-spin" />
        <p className="text-neutral-400">Yükleniyor...</p>
      </div>
    );
  }
  if (authState === "out") return <LoginScreen />;

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Toaster theme="dark" position="top-center" richColors />
      <header className="sticky top-0 z-20 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-dark.png" alt="Renewolt" className="h-9 object-contain" />
            <span className="hidden sm:inline text-neutral-500 text-sm border-l border-white/10 pl-3">Yönetim</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />}
            <span className="hidden sm:inline text-sm text-neutral-300">{user?.name || user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white border border-white/10 rounded-full px-4 py-2 transition-colors">
              <LogOut size={15} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display font-light text-4xl tracking-tighter text-white">Gelen Mesajlar</h1>
            <p className="text-neutral-400 mt-2 text-sm">
              Toplam {messages.length} mesaj · <span className="text-[#5CC528]">{unread} okunmamış</span>
            </p>
          </div>
          <button onClick={fetchMessages} data-testid="refresh-btn" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#5CC528] border border-white/10 rounded-full px-4 py-2 transition-colors">
            <RefreshCw size={15} className={loadingMsgs ? "animate-spin" : ""} /> Yenile
          </button>
        </div>

        {loadingMsgs && messages.length === 0 ? (
          <div className="flex justify-center py-20"><Loader2 size={32} className="text-[#5CC528] animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Inbox size={48} className="text-neutral-700 mb-4" />
            <p className="text-neutral-400">Henüz mesaj yok.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="messages-list">
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`message-${m.id}`}
                className={`p-6 rounded-2xl border transition-colors ${m.read ? "bg-[#0f0f0f] border-white/5" : "bg-[#121212] border-[#5CC528]/30"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold text-lg text-white">{m.name}</h3>
                      {!m.read && <span className="text-[10px] uppercase tracking-wider bg-[#5CC528] text-black px-2 py-0.5 rounded-full font-bold">Yeni</span>}
                      {m.subject && <span className="text-xs text-neutral-500">· {m.subject}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-neutral-400 flex-wrap">
                      <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-[#5CC528]"><Mail size={14} /> {m.email}</a>
                      {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#5CC528]"><Phone size={14} /> {m.phone}</a>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!m.read && (
                      <button onClick={() => markRead(m.id)} data-testid={`mark-read-${m.id}`} className="inline-flex items-center gap-1.5 text-xs bg-[#5CC528]/15 text-[#5CC528] hover:bg-[#5CC528] hover:text-black px-3 py-2 rounded-lg transition-colors">
                        <Check size={14} /> Okundu
                      </button>
                    )}
                    <button onClick={() => remove(m.id)} data-testid={`delete-${m.id}`} className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-red-400 hover:bg-red-500/10 px-3 py-2 rounded-lg transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-neutral-300 text-sm leading-relaxed border-l-2 border-white/10 pl-4">{m.message}</p>
                <p className="mt-3 text-xs text-neutral-600">{new Date(m.created_at).toLocaleString("tr-TR")}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
